import React, { useCallback, useEffect, useState } from 'react'
import { View, Text as RNText, TextStyle, Pressable } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import { Instance } from 'mobx-state-tree'

import { useStores, setPageTitle, FilterTermsProps } from '@trivie/core'
import { AssignedTopic } from '@trivie/core/src/models/AssignedTopic'
import { color } from '@trivie/ui-web/theme'
import { TopicCard } from '@trivie/ui-mobile'
const _sortBy = require('lodash/sortBy')

export interface TopicsTabProps {
  searchTerm?: string
  filterTerms?: FilterTermsProps
  clearFilters: () => void
}

export const TopicsTab = observer((props: TopicsTabProps) => {
  const { libraryStore, userStore } = useStores()
  const { topicsSansGeneral } = libraryStore
  const { clearFilters, searchTerm, filterTerms } = props

  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })

  const history = useHistory()

  const goToTopic = (topic: Instance<typeof AssignedTopic>) => {
    setPageTitle(`${topic.topic.title} - ${process.env.REACT_APP_SITE_TITLE}`)
    if (topic.subscription_user_id) {
      history.push(
        `/library/topic/?subscription_user_id=${topic.metadata.subscription_user_id}&topic_id=${topic.topic.topic_id}&path_id=${topic.metadata.path_id}&content_user_id=${topic.metadata.content_user_id}`,
      )
    } else {
      history.push(
        `/library/topic/?assignment_id=${topic.metadata.assignment_id}&topic_id=${topic.topic.topic_id}&path_id=${topic.metadata.path_id}&content_user_id=${topic.metadata.content_user_id}`,
      )
    }
  }

  function useWindowSize() {
    useEffect(() => {
      function handleResize() {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        })
      }
      window.addEventListener('resize', handleResize)
      handleResize()
      return () => window.removeEventListener('resize', handleResize)
    }, [])
    return windowSize
  }

  useWindowSize()
  function responsiveGrid() {
    if (windowSize.width < 1024) {
      return THINGRID
    } else if (windowSize.width < 1440) {
      return NORMALGRID
    } else {
      return WIDEGRID
    }
  }

  const getData = useCallback(() => {
    const fullData = topicsSansGeneral
      .slice()
      ?.filter(
        (f) =>
          !searchTerm || f.topic.title.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()),
      )

    if (!filterTerms?.status || filterTerms.status.length === 0) return fullData

    let result: typeof fullData = []

    if (filterTerms?.status.includes('1')) {
      result = result.concat(
        fullData.filter((f) => {
          return f?.topic?.pct_complete === 0
        }),
      )
    }

    //in progress
    if (filterTerms?.status.includes('2')) {
      result = result.concat(
        fullData.filter(
          (f) => Number(f.topic.pct_complete) > 0 && Number(f.topic.pct_complete) < 1,
        ),
      )
    }

    //completed
    if (filterTerms?.status.includes('3')) {
      result = result.concat(fullData.filter((f) => f.topic.pct_complete === 1))
    }
    //locked
    if (filterTerms?.status.includes('3')) {
      result = result.concat(fullData.filter((f) => f.locked_by_pre_test))
    }
    return result
  }, [topicsSansGeneral, filterTerms, searchTerm])

  const filteredData = _sortBy(
    getData(),
    [(topic) => topic?.topic?.pct_complete === 1, (topic) => topic?.topic?.title.toLowerCase()],
    ['asc'],
  )

  return (
    <div style={CONTENT}>
      <div style={responsiveGrid()}>
        {filteredData.map((topic: any, index: number) => {
          return (
            <TopicCard
              // @ts-ignore
              dataSet={{ qat: `topic-card-${index}` }}
              topic={topic}
              hideBg={userStore.user?.workspace_settings?.is_subscription_workspace}
              onPress={goToTopic}
              key={`${topic.title}_${index}`}
            />
          )
        })}
      </div>
      {filteredData?.length !== topicsSansGeneral.length ? (
        <View style={{ paddingHorizontal: 40, paddingTop: filteredData?.length === 0 ? 120 : 12 }}>
          <RNText style={FILTER}>
            {`${
              topicsSansGeneral.length - filteredData?.length
            } other topics are not shown due to your active filters.`}
            <Pressable onPress={() => clearFilters()}>
              <RNText style={CLEAR_FILTER}>{'Clear Filters?'}</RNText>
            </Pressable>
          </RNText>
        </View>
      ) : null}
    </div>
  )
})

/**
 * Styling
 */
const CONTENT: React.CSSProperties = {
  backgroundColor: color.white,
  height: '100%',
}
const WIDEGRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: '1fr',
  gridGap: 20,
  paddingTop: 0,
  paddingBottom: 20,
}
const NORMALGRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: '1fr',
  gridGap: 20,
  paddingTop: 0,
  paddingBottom: 20,
}
const THINGRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridTemplateRows: '1fr',
  gridGap: 20,
  paddingTop: 0,
  paddingBottom: 20,
}
const FILTER: TextStyle = {
  alignSelf: 'center',
  textAlign: 'center',
  fontStyle: 'italic',
  color: color.shade50,
}
const CLEAR_FILTER: TextStyle = {
  ...FILTER,
  alignSelf: 'center',
  paddingTop: 40,
  paddingLeft: 4,
  textDecorationLine: 'underline',
}
